import { WebApiInterop } from "./WebApiInterop";
import { EmptyServiceReadings, ServiceReadings, ServiceReadingsSet, EmptyServiceReadingsSet, Reading } from "../model/Reading";
import { ServiceSmokeFull, EmptyServiceSmokeFull, SmokeFull } from "../model/SmokeFull";
import { ServiceTherms, EmptyServiceTherms, Therm } from "../model/Therm";
import { ServiceSmokes, EmptyServiceSmokes, Smoke } from "../model/Smoke";
import { EmptyServiceVentLogs, ServiceVentLogs, VentLog } from "../model/VentLog";
import { RexDateTime } from "../Dates/RexDateTime";
import { EmptyServiceResult, ServiceResult, TServiceResult } from "../model/TServiceResult";
import { Meat } from "../model/Meat";

export class SmokeApi
{
    m_api: WebApiInterop;

    constructor(api: WebApiInterop)
    {
        this.m_api = api;
    }

    get isAuthenticated(): boolean
    {
        return this.m_api.isAuthenticated;
    }

    /*----------------------------------------------------------------------------
        %%Function: setAuth
    ----------------------------------------------------------------------------*/
    setAuth(token: string): void
    {
        this.m_api.setAuth(token);
    }

    async getOutOfBoundsReadingsCountForSmoke(smokeId: string): Promise<TServiceResult<number>>
    {
        const response = await this.m_api.Fetch<TServiceResult<number>>(
            "api/smoke/getOutOfBoundsReadingsCount",
            [
                {
                    SmokeID: smokeId
                }
            ],
            {
                ...EmptyServiceResult,
                TheValue: 0, // Default value for count
            });

        return response;
    }

    async deleteOutOfBoundsReadingsForSmoke(smokeId: string): Promise<ServiceResult>
    {
        const response = await this.m_api.FetchWithAuth<ServiceResult>(
            "api/smoke/deleteOutOfBoundsReadings",
            [
                {
                    SmokeID: smokeId
                }
            ],
            EmptyServiceResult);

        return response;
    }


    /*----------------------------------------------------------------------------
        %%Function: getLatestReadings
    ----------------------------------------------------------------------------*/
    async getLatestReadings(readings?: number, minutes?: number, address?: number, smokeId?: string): Promise<ServiceReadings>
    {
        const response = await this.m_api.Fetch<ServiceReadings>(
            "api/reading/getLatestReadings",
            [
                {
                    Readings: readings ?? 180,
                    Minutes: minutes ?? 0,
                    Address: address ?? 0,
                    SmokeID: smokeId ?? ""
                }
            ],
            EmptyServiceReadings
        );

        if (response.TheValue)
        {
            for (const reading of response.TheValue)
                reading.Timestamp = new RexDateTime((reading.Timestamp as Date).valueOf());
        }

        return response;
    }

    /*----------------------------------------------------------------------------
        %%Function: convertMaybeDateStringToDate
    ----------------------------------------------------------------------------*/
    convertMaybeDateStringToDate(date: Date | string): Date
    {
        if (typeof (date) === "string")
        {
            if (date.indexOf("+") < 0 && date.lastIndexOf("-") < date.length - 5)
                date = `${date}Z`;

            date = new Date(date);
        }

        return date;
    }

    /*----------------------------------------------------------------------------
        %%Function: getLatestReadingsSet
    ----------------------------------------------------------------------------*/
    async getLatestReadingsSet(readings?: number, minutes?: number, address?: number, smokeId?: string): Promise<ServiceReadingsSet>
    {
        const response = await this.m_api.Fetch<ServiceReadingsSet>(
            "api/reading/getLatestReadingsSet",
            [
                {
                    Readings: readings ?? 180,
                    Minutes: minutes ?? 0,
                    Address: address ?? 0,
                    SmokeID: smokeId ?? ""
                }
            ],
            EmptyServiceReadingsSet
        );

        if (response.TheValue)
        {
            for (const readingSet of response.TheValue)
            {
                for (const reading of readingSet)
                    reading.Timestamp = new RexDateTime((reading.Timestamp as Date).valueOf());
            }
        }

        return response;
    }

    /*----------------------------------------------------------------------------
        %%Function: getSmokeFull
    ----------------------------------------------------------------------------*/
    async getSmokeFull(smokeID: string): Promise<ServiceSmokeFull>
    {
        const response = await this.m_api.Fetch<ServiceSmokeFull>(
            "api/smoke/getSmokeFull",
            [
                {
                    SmokeID: smokeID
                }
            ],
            EmptyServiceSmokeFull
        );

        return response;
    }

    /*----------------------------------------------------------------------------
        %%Function: updateSmoke
    ----------------------------------------------------------------------------*/
    async updateSmoke(smoke: Smoke)
    {
        const smokeService = { ...smoke };

        smokeService.Date = new Date((smokeService.Date as RexDateTime).utcValue);

        const response = await this.m_api.FetchPostWithAuth(
            "api/smoke/updateSmoke",
            smokeService);
    }

    async updateMeat(meat: Meat)
    {
        const response = await this.m_api.FetchPostWithAuth(
            "api/meat/updateMeat",
            meat);
    }

    async addMeat(meat: Meat)
    {
        const response = await this.m_api.FetchPostWithAuth(
            "api/meat/addMeat",
            meat);
    }

    async deleteMeat(meatId: string): Promise<ServiceResult>
    {
        const response = await this.m_api.FetchWithAuth<ServiceResult>(
            "api/meat/deleteMeat",
            [
                {
                    MeatID: meatId
                }
            ],
            EmptyServiceResult);

        return response;
    }

    async deleteTherm(therm: Therm): Promise<ServiceResult>
    {
        const response = await this.m_api.FetchPostWithAuth<ServiceResult>(
            "api/therm/deleteTherm",
            therm);

        return response;
    }

    async addTherm(therm: Therm): Promise<ServiceResult>
    {
        const response = await this.m_api.FetchPostWithAuth<ServiceResult>(
            "api/therm/addTherm",
            therm);
        return response;
    }

    async updateTherm(therm: Therm): Promise<ServiceResult>
    {
        const response = await this.m_api.FetchPostWithAuth<ServiceResult>(
            "api/therm/updateTherm",
            therm);
        return response;
    }

    async addVentLog(ventLog: VentLog): Promise<ServiceResult>
    {
        const ventLogService = { ...ventLog };
        ventLogService.Timestamp = new Date((ventLogService.Timestamp as RexDateTime).utcValue);

        const response = await this.m_api.FetchPostWithAuth<ServiceResult>(
            "api/ventlog/addVentLog",
            ventLogService);
        return response;
    }

    async updateVentLog(ventLog: VentLog): Promise<ServiceResult>
    {
        const ventLogService = { ...ventLog };
        ventLogService.Timestamp = new Date((ventLogService.Timestamp as RexDateTime).utcValue);

        const response = await this.m_api.FetchPostWithAuth<ServiceResult>(
            "api/ventlog/updateVentLog",
            ventLogService);
        return response;
    }

    async deleteVentLog(ventLog: VentLog): Promise<ServiceResult>
    {
        const ventLogService = { ...ventLog };
        ventLogService.Timestamp = new Date((ventLogService.Timestamp as RexDateTime).utcValue);

        const response = await this.m_api.FetchPostWithAuth<ServiceResult>(
            "api/ventlog/deleteVentLog",
            ventLogService);
        return response;
    }

    /*----------------------------------------------------------------------------
        %%Function: addSmoke
    ----------------------------------------------------------------------------*/
    async addSmoke(smokeFull: SmokeFull)
    {
        const smokeService = { ...smokeFull };

        smokeService.Date = new Date((smokeService.Date as RexDateTime).utcValue);

        const response = await this.m_api.FetchPostWithAuth(
            "api/smoke/addSmokeFull",
            smokeService);
    }


    /*----------------------------------------------------------------------------
        %%Function: getTherms
    ----------------------------------------------------------------------------*/
    async getTherms(smokeID: string): Promise<ServiceTherms>
    {
        const response = await this.m_api.Fetch<ServiceTherms>(
            "api/therm/getTherms",
            [
                {
                    SmokeID: smokeID
                }
            ],
            EmptyServiceTherms
        );
        return response;
    }

    /*----------------------------------------------------------------------------
        %%Function: getSmokesForAuthUser
    ----------------------------------------------------------------------------*/
    async getSmokesForAuthUser(count: number): Promise<ServiceSmokes>
    {
        const response = await this.m_api.FetchWithAuth<ServiceSmokes>(
            "api/smoke/getSmokesForAuthUser",
            [
                {
                    SmokeCount: count
                }
            ],
            EmptyServiceSmokes);

        if (response.TheValue)
        {
            for (const smoke of response.TheValue)
                smoke.Date = new RexDateTime((smoke.Date as Date).valueOf());
        }

        return response;
    }

    async deleteSmoke(smokeId: string): Promise<ServiceResult>
    {
        const response = await this.m_api.FetchWithAuth<ServiceResult>(
            "api/smoke/deleteSmoke",
            [
                {
                    SmokeID: smokeId
                }
            ],
            EmptyServiceResult);

        return response;
    }

    /*----------------------------------------------------------------------------
        %%Function: getVentLogs
    ----------------------------------------------------------------------------*/
    async getVentLogs(smokeID: string, count: number): Promise<ServiceVentLogs>
    {
        const response = await this.m_api.Fetch<ServiceVentLogs>(
            "api/ventlog/getVentLogs",
            [
                {
                    SmokeID: smokeID,
                    LogCount: count
                }
            ],
            EmptyServiceVentLogs
        );

        if (response.TheValue)
        {
            for (const log of response.TheValue)
                log.Timestamp = new RexDateTime((log.Timestamp as Date).valueOf());
        }
        return response;
    }
}